import { inject } from "@angular/core";
import {
  CanActivateFn,
  CanMatchFn,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from "@angular/router";
import { AuthService } from "../service/auth.service";
import { PasswordExpiryService } from "../service/password-expiry.service";
import { firstValueFrom, timeout, catchError, of } from "rxjs";

export const noAuthGuard: CanActivateFn | CanMatchFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return new Promise<boolean>((resolve) => {
    firstValueFrom(authService.isLoggedIn())
      .then((isLoggedIn) => {
        if (isLoggedIn) {
          router.navigate(["/"]);
          resolve(false);
        } else {
          resolve(true);
        }
      })
      .catch(() => resolve(true));
  });
};

export const loggedInGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const redirectToLogin = () => {
    // Prefer state.url (target of navigation) over router.url (current
    // URL), since router.url is still the previous page during the guard.
    const targetUrl =
      (state && typeof state !== "string" && state.url) ||
      (typeof state === "string" ? state : router.url) ||
      "";

    if (targetUrl && targetUrl !== "/auth/login") {
      // Persist in sessionStorage for the existing redirect-after-login path
      // (back-compat with other flows), AND surface in the URL bar so the
      // user can see/share/refresh the login URL.
      sessionStorage.setItem("redirectUrl", targetUrl);
      router.navigate(["/auth/login"], {
        queryParams: { returnUrl: targetUrl },
      });
    } else {
      router.navigate(["/auth/login"]);
    }
  };

  return new Promise<boolean>((resolve) => {
    firstValueFrom(authService.isLoggedIn())
      .then((isLoggedIn) => {
        if (!isLoggedIn) {
          redirectToLogin();
          resolve(false);
          return;
        }
        resolve(true);
      })
      .catch(() => {
        redirectToLogin();
        resolve(false);
      });
  });
};

/**
 * Guard that blocks access to main app routes when password is expired.
 * Redirects to change-password. Applied to main layout; change-password route uses only loggedInGuard.
 */
export const passwordNotExpiredGuard: CanActivateFn | CanMatchFn = () => {
  const passwordExpiryService = inject(PasswordExpiryService);
  const authService = inject(AuthService);
  const router = inject(Router);

  return new Promise<boolean>((resolve) => {
    if (passwordExpiryService.isExpired) {
      router.navigate(["/auth/change-password"]);
      resolve(false);
      return;
    }

    firstValueFrom(
      authService.getClaims().pipe(
        timeout(2000),
        catchError(() => of(null))
      )
    )
      .then((claims) => {
        if (
          claims &&
          passwordExpiryService.checkClaimsExpiry(claims as any)
        ) {
          passwordExpiryService.setExpired();
          router.navigate(["/auth/change-password"]);
          resolve(false);
          return;
        }
        resolve(true);
      })
      .catch(() => {
        resolve(true);
      });
  });
};

// export const adminGuard: CanActivateFn | CanMatchFn = () => {
//   const authService = inject(AuthService);
//   const router = inject(Router);

//   return new Promise<boolean>((resolve) => {
//     firstValueFrom(authService.isLoggedIn())
//       .then((isLoggedIn) => {
//         if (!isLoggedIn) {
//           router.navigate(["/auth/login"]);
//           resolve(false);
//           return;
//         }

//         return firstValueFrom(authService.getClaims());
//       })
//       .then((claims) => {
//         if (!claims) {
//           router.navigate(["/"]);
//           resolve(false);
//           return;
//         }

//         const isAdmin = claims.role === "admin";
//         if (!isAdmin) {
//           router.navigate(["/"]);
//           resolve(false);
//           return;
//         }

//         resolve(true);
//       })
//       .catch(() => {
//         router.navigate(["/auth/login"]);
//         resolve(false);
//       });
//   });
// };

// export const staffGuard: CanActivateFn | CanMatchFn = () => {
//   const authService = inject(AuthService);
//   const router = inject(Router);

//   return new Promise<boolean>((resolve) => {
//     firstValueFrom(authService.isLoggedIn())
//       .then((isLoggedIn) => {
//         if (!isLoggedIn) {
//           router.navigate(["/auth/login"]);
//           resolve(false);
//           return;
//         }

//         return firstValueFrom(authService.getClaims());
//       })
//       .then((claims) => {
//         if (!claims) {
//           router.navigate(["/"]);
//           resolve(false);
//           return;
//         }

//         const isStaff = claims.role === "admin" || claims.role === "staff";
//         if (!isStaff) {
//           router.navigate(["/"]);
//           resolve(false);
//           return;
//         }

//         resolve(true);
//       })
//       .catch(() => {
//         router.navigate(["/auth/login"]);
//         resolve(false);
//       });
//   });
// };

// export const clinicianGuard: CanActivateFn | CanMatchFn = () => {
//   const authService = inject(AuthService);
//   const router = inject(Router);

//   return new Promise<boolean>((resolve) => {
//     firstValueFrom(authService.isLoggedIn())
//       .then((isLoggedIn) => {
//         if (!isLoggedIn) {
//           router.navigate(["/auth/login"]);
//           resolve(false);
//           return;
//         }

//         return firstValueFrom(authService.getClaims());
//       })
//       .then((claims) => {
//         if (!claims) {
//           router.navigate(["/"]);
//           resolve(false);
//           return;
//         }

//         const isClinician = claims.role === "clinician";
//         if (!isClinician) {
//           router.navigate(["/"]);
//           resolve(false);
//           return;
//         }

//         resolve(true);
//       })
//       .catch(() => {
//         router.navigate(["/auth/login"]);
//         resolve(false);
//       });
//   });
// };
